import { MapPin, Phone, Mail, Clock, ArrowRight, Facebook, Instagram } from "lucide-react";

const BOOK_URL = "https://kneadrelief.noterro.com/";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative pt-20 sm:pt-28 lg:pt-32 bg-[#071F36] text-white overflow-hidden"
    >
      <div className="absolute inset-0 -z-0 opacity-[0.18]">
        <img
          src="https://images.pexels.com/photos/31228635/pexels-photo-31228635.jpeg"
          alt=""
          aria-hidden
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#071F36] via-[#071F36]/80 to-[#071F36]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Big CTA */}
        <div className="text-center max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-sky-300 mb-4">
            Let's begin this chapter together
          </div>
          <h2
            data-testid="contact-heading"
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02]"
          >
            It's time to <span className="text-sky-300">live again.</span>
          </h2>
          <p className="mt-6 text-lg text-blue-100/80 max-w-2xl mx-auto leading-relaxed">
            Reach out and book your appointment today. Let this be the turning point in your story.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-book-now"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-blue-50 rounded-full px-7 py-4 font-semibold transition-all hover:-translate-y-0.5 shadow-xl shadow-black/20"
            >
              Book Your Appointment
              <ArrowRight size={18} strokeWidth={2.2} />
            </a>
            <a
              href="mailto:info@kneadrelief.ca"
              data-testid="contact-email-cta"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white rounded-full px-7 py-4 font-semibold transition-all"
            >
              Email Dennis
            </a>
          </div>
        </div>

        {/* Contact grid */}
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: MapPin,
              label: "Visit",
              value: "2 Bowbank Crescent NW",
              sub: "Calgary, AB",
              href: "#locations",
              testid: "contact-address",
            },
            {
              icon: Phone,
              label: "Call",
              value: "403-714-7058",
              sub: "Mon–Sat",
              href: "tel:+14037147058",
              testid: "contact-phone",
            },
            {
              icon: Mail,
              label: "Email",
              value: "info@kneadrelief.ca",
              sub: "Replies within 24h",
              href: "mailto:info@kneadrelief.ca",
              testid: "contact-email",
            },
            {
              icon: Clock,
              label: "Hours",
              value: "By appointment",
              sub: "Online booking 24/7",
              href: BOOK_URL,
              testid: "contact-hours",
            },
          ].map((c) => (
            <a
              key={c.label}
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-testid={c.testid}
              className="group bg-white/[0.05] backdrop-blur-sm border border-white/10 hover:bg-white/[0.08] hover:border-sky-300/30 rounded-2xl p-6 transition-all"
            >
              <c.icon size={20} strokeWidth={1.6} className="text-sky-300 mb-3" />
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-200">
                {c.label}
              </div>
              <div className="mt-1 font-display font-bold text-lg text-white group-hover:text-sky-200 transition-colors">
                {c.value}
              </div>
              <div className="text-sm text-blue-100/60 mt-0.5">{c.sub}</div>
            </a>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-20 pb-10 border-t border-white/10 pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-4">
              <div
                aria-label="KneadRelief bullseye mark"
                className="h-24 w-[92px] shrink-0 bg-no-repeat"
                style={{
                  backgroundImage:
                    "url(https://customer-assets-cm19k8pv.emergentagent.net/job_knead-refresh/artifacts/tftgtah7_KneadRelief_logo_transparent_highres.webp)",
                  backgroundPosition: "-4px center",
                  backgroundSize: "340px auto",
                }}
              />
              <div>
                <div className="font-display font-bold text-white tracking-tight leading-none text-3xl">
                  KneadRelief
                </div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/80 font-semibold mt-2">
                  Pain Relief · Movement · Recovery
                </div>
                <div className="text-xs text-blue-200/70 mt-1.5">Calgary, AB</div>
              </div>
            </div>
            <p className="text-xs text-blue-200/60 mt-4 max-w-md">
              © {new Date().getFullYear()} KneadRelief. Operated by Dennis Lee, RMT.
              RAPID NeuroFascial Reset Specialist. All rights reserved.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://www.facebook.com/KneadReliefCalgary/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              aria-label="Facebook"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <Facebook size={18} strokeWidth={1.6} />
            </a>
            <a
              href="https://www.instagram.com/kneadrelief/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="Instagram"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <Instagram size={18} strokeWidth={1.6} />
            </a>
            <a
              href="https://www.tiktok.com/@kneadrelief"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-tiktok"
              aria-label="TikTok"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.85a8.16 8.16 0 0 0 4.77 1.52V6.92a4.85 4.85 0 0 1-1.84-.23Z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
